import lodashCamelCase from 'lodash/camelCase';
import { 
  STATUS_CLOSED,
  STATUS_VOID,
  STATUS_OPEN,
  STATUS_ANSWERED,
  STATUS_REJECTED,
  STATUS_OPEN_REV1,
  STATUS_OPEN_REV2,
  STATUS_ANSWERED_REV1,
  STATUS_REJECTED_REV1,
  STATUS_SUBMITTED,
} from '../constants/status-types';
import {
  FE_TRANSITION_FIELDS, INFO_TRANSLATION_KEYS,
  CALL_TO_ACTION_LABEL_TRANSLATION_KEYS, CLOSED_TRANSITION_KEYS, VOID_TRANSITION_KEYS,
  IGNORED_CALL_TO_ACTION_TRANSITION_STATUSES, IGNORED_CALL_TO_ACTION_TRANSITION_STATUSES_REJECTED_RFI, TRANSITION_SAVE_BUTTON_TRANSLATION_KEYS,
  ANSWER_TRANSITION_KEYS, BE_TRANSITION_KEYS,
} from '../constants/transitions';
import { ISSUE_ATTRIBUTE_NAMES_MAP, ISSUE_ATTRIBUTE_NAMES } from '../constants/issue-attribute-names';
import { getPermittedStatuses } from './issues-helpers';
import featureFlags, { FEATURE_FLAGS } from '../utils/featureFlags';
import T from 'i18n-react';

export const getTransitionStatus = (transition) => {
  return transition.serverStatus || transition.status;
};

export const getInfoTextTranslationKey = (transition) => {
  const key = transition.key;
  if (CLOSED_TRANSITION_KEYS.includes(key)) {
    return INFO_TRANSLATION_KEYS.CLOSED_TRANSITION;
  }
  if (VOID_TRANSITION_KEYS.includes(key)) {
    return INFO_TRANSLATION_KEYS.VOID_TRANSITION;
  }
  return INFO_TRANSLATION_KEYS[key];
};

export const getInfoTextTranslationKey_v2 = (transition, currentStatus) => {
  const status = transition.serverStatus;
  const trimmedStatus = getStatusFromServerStatus(status);
  const isReopenTransition = (currentStatus === STATUS_CLOSED &&  (trimmedStatus === STATUS_ANSWERED || trimmedStatus === STATUS_REJECTED || trimmedStatus === STATUS_SUBMITTED));
  // reopen info text
  if (isReopenTransition) {
    return INFO_TRANSLATION_KEYS.REOPEN_TRANSITION;
  }
  switch (status) {
    case STATUS_CLOSED:
      return INFO_TRANSLATION_KEYS.CLOSED_TRANSITION;
    case STATUS_VOID:
      return INFO_TRANSLATION_KEYS.VOID_TRANSITION;
    case STATUS_OPEN_REV2:
      if (currentStatus === STATUS_ANSWERED_REV1 || currentStatus === STATUS_REJECTED_REV1) {
        return INFO_TRANSLATION_KEYS[STATUS_OPEN];
      } else return INFO_TRANSLATION_KEYS[status];

    default:
      return INFO_TRANSLATION_KEYS[status];
  }
};

const getTransitionAttributes_v2 = (transition, currentStatus) => {
  const transitionFields = transition.required_attributes.map(field => {
    return {
      fieldName: ISSUE_ATTRIBUTE_NAMES_MAP[field] || field,
      isMandatory: true,
      values: transition.values[field],
    };
  });
  transitionFields.push(...transition.optionalAttributes.map(({ fieldName, isMandatory }) => {
    return {
      fieldName: ISSUE_ATTRIBUTE_NAMES_MAP[fieldName] || fieldName,
      isMandatory,
      values: transition.values[fieldName],
    };
  }));
  // For reopen form -> Add custom-id attribute in the end of form
  if (currentStatus === STATUS_CLOSED) {
    transitionFields.push({
      fieldName: ISSUE_ATTRIBUTE_NAMES.CUSTOM_IDENTIFIER,
      isMandatory: false,
      sortOrder: 1,
      disabled: true,
      readOnly: false,
    });
  }
  const infoTextTranslationKey = getInfoTextTranslationKey_v2(transition, currentStatus);
  if (infoTextTranslationKey) {
    transitionFields.push({
      fieldName: FE_TRANSITION_FIELDS.INFO,
      translationKey: infoTextTranslationKey,
    });
  }
  return transitionFields;
};
const getTransitionAttributes_v1 = (transition) => {
  const beTransitionFields = (transition.transition_attributes || []).map(field => {
    return {
      fieldName: lodashCamelCase(field.id),
      isMandatory: field.is_required,
      values: field.values,
    };
  });
  const infoTextTranslationKey = getInfoTextTranslationKey(transition);
  if (infoTextTranslationKey) {
    beTransitionFields.push({
      fieldName: FE_TRANSITION_FIELDS.INFO,
      translationKey: infoTextTranslationKey,
    });
  }
  return beTransitionFields;
};
export const getTransitionAttributes = (transition, currentStatus) => {
  return transition.v2 ? getTransitionAttributes_v2(transition, currentStatus) : getTransitionAttributes_v1(transition);
};

const createBasicTransitionOption = (transition, currentStatus) => {
  const value = getTransitionStatus(transition);
  return {
    value,
    transitionId: transition.id || transition.status,
    key: transition.key || transition.serverStatus,
    transitionAttributes: getTransitionAttributes(transition, currentStatus),
  };
};

export const createTransitionStatusOption = (transition, currentStatus) => {
  const baseTransitionOption = createBasicTransitionOption(transition, currentStatus);
  const status = baseTransitionOption.transitionId;
  return {
    ...baseTransitionOption,
    label: T.translate(`status.${status}`),
    colorClassName: `IssueStatus__${status}`,
  };
};

export const getStatusFromServerStatus = (serverStatus) => {
  return serverStatus.replace(new RegExp(/(Rev1|Rev2)$/), "").replace(new RegExp(/(Manager)$/), "");
};

const getCallToActionLabelKey = (transition, issue) => {
  const key = transition.key;
  const serverKey = getStatusFromServerStatus(key);
  const trimmedServerStatus = getStatusFromServerStatus(issue.status);
  const isReopenPermitted =
    trimmedServerStatus === STATUS_CLOSED &&
    (serverKey === STATUS_ANSWERED ||
      serverKey === STATUS_REJECTED ||
      serverKey === STATUS_SUBMITTED);

  if (CLOSED_TRANSITION_KEYS.includes(key)) {
    return CALL_TO_ACTION_LABEL_TRANSLATION_KEYS.CLOSED_TRANSITION;
  }

  if ([STATUS_ANSWERED, STATUS_REJECTED].includes(getStatusFromServerStatus(issue.status))) {
    switch (key) {
      case STATUS_OPEN:
      case STATUS_OPEN_REV1:
        return CALL_TO_ACTION_LABEL_TRANSLATION_KEYS.RFI_TRANSITION_GC_ARCH_REOPEN;
      case STATUS_OPEN_REV2:
        return CALL_TO_ACTION_LABEL_TRANSLATION_KEYS.RFI_TRANSITION_GC_ARCH_REOPEN_REV2;
      default:
        break;
    }
  }
  // show reopen call to action label
  if(isReopenPermitted) {
    return CALL_TO_ACTION_LABEL_TRANSLATION_KEYS.REOPEN_TRANSITION;
  }

  return CALL_TO_ACTION_LABEL_TRANSLATION_KEYS[key];
};

const isTransitionInOptions = (issue, transition) => {
  if (issue.status === STATUS_REJECTED) {
    return !IGNORED_CALL_TO_ACTION_TRANSITION_STATUSES_REJECTED_RFI.includes(transition.status);
  }
  // show reopen for closed status as per the previous status (answered/rejected/submitted)
  if (issue.status === STATUS_CLOSED) {
    if (
      featureFlags.get(FEATURE_FLAGS.RFIS_REOPEN) &&
      (transition.status === STATUS_REJECTED ||
        transition.status === STATUS_ANSWERED ||
        transition.status === STATUS_SUBMITTED)
    ) {
      return true;
    }
    return false;
  }
  return !IGNORED_CALL_TO_ACTION_TRANSITION_STATUSES.includes(transition.status);
};

export const getCallToActionOptions = (issue) => {
  const getCallToActionOptions = getPermittedStatuses(issue)
    .filter(transition => isTransitionInOptions(issue, transition))
    .map(transition => {
      const baseTransitionOption = createBasicTransitionOption(transition, issue.serverStatus);
      return {
        ...baseTransitionOption,
        label: T.translate(getCallToActionLabelKey(baseTransitionOption, issue)),
      };
    });
  const isAnsweredOrRejectedRev2 = [BE_TRANSITION_KEYS.RFI_TRANSITION_ARCH_CM_ANSWER_V2, BE_TRANSITION_KEYS.RFI_TRANSITION_ARCH_CM_REJECT_V2].includes(issue.serverStatus);
  return isAnsweredOrRejectedRev2 ? getCallToActionOptions.reverse() : getCallToActionOptions;
};

export const getTransitionSaveButtonTextKey = (transition, currentStatus) => {
  const transitionKey = transition.key || transition.value;
  let translationKey;
  const serverTransitionStatus = getStatusFromServerStatus(transitionKey);
  const isReopenStatus =
    currentStatus === STATUS_CLOSED &&
    (serverTransitionStatus === STATUS_ANSWERED ||
      serverTransitionStatus === STATUS_REJECTED ||
      serverTransitionStatus === STATUS_SUBMITTED);

  if (CLOSED_TRANSITION_KEYS.includes(transitionKey)) {
    translationKey = TRANSITION_SAVE_BUTTON_TRANSLATION_KEYS.CLOSED_TRANSITION;
  } else if (VOID_TRANSITION_KEYS.includes(transitionKey)) {
    translationKey = TRANSITION_SAVE_BUTTON_TRANSLATION_KEYS.VOID_TRANSITION;
  } else if(isReopenStatus) {
    translationKey = TRANSITION_SAVE_BUTTON_TRANSLATION_KEYS.REOPEN_TRANSITION;
  } else {
    translationKey = TRANSITION_SAVE_BUTTON_TRANSLATION_KEYS[transitionKey];
  }
  return translationKey;
};

export const isChangesetIncludeAnswer = (item) => {
  return item.type === 'changesets' && (item.changes.answer || item.changes.officialResponse);
};

export const isOfficialResponseEnabled = (permittedTransition) => {
  return ANSWER_TRANSITION_KEYS.includes(permittedTransition.key);
};
